import { useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@bri/shared-components';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { getErrorMessage } from '@/lib/utils';
import toast from 'react-hot-toast';
import { CheckCircleIcon, EnvelopeIcon } from '@heroicons/react/24/outline';

export default function VerifyCodePage() {
  const [verificationCode, setVerificationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const { confirmSignUp, resendVerificationCode } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  const email = location.state?.email || '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!verificationCode.trim()) {
      toast.error('Please enter the verification code');
      return;
    }

    if (!email) {
      toast.error('Email address not found. Please try signing up again.');
      return;
    }

    setIsLoading(true);
    
    try {
      await confirmSignUp(email, verificationCode);
      toast.success('Email verified successfully! You can now sign in.');
      navigate('/auth/login');
    } catch (error: unknown) {
      console.error('Verification error:', error);
      const errorMessage = getErrorMessage(error);
      
      // Check if this is a PostConfirmation Lambda error but user is actually verified
      if (errorMessage.includes('PostConfirmation failed')) {
        // User might actually be verified, show success but mention the issue
        toast.success('Email verified successfully! You can now sign in. (Note: Some background processing may have failed, but your account is active.)');
        navigate('/auth/login');
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!email) {
      toast.error('Email address not found');
      return;
    }

    setIsResending(true);
    
    try {
      await resendVerificationCode(email);
      toast.success('Verification code sent!');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-primary/10 rounded-full flex items-center justify-center">
            <EnvelopeIcon className="h-8 w-8 text-primary" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-foreground">
            Verify your email
          </h2>
          <p className="mt-2 text-center text-sm text-muted-foreground">
            We've sent a verification code to{' '}
            {email && (
              <span className="font-medium text-foreground">{email}</span>
            )}
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div>
            <label htmlFor="verificationCode" className="form-label">
              Verification code
            </label>
            <input
              id="verificationCode"
              name="verificationCode"
              type="text"
              autoComplete="one-time-code"
              required
              className="form-input text-center text-lg tracking-widest"
              placeholder="Enter 6-digit code"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              disabled={isLoading}
              maxLength={6}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Enter the 6-digit code from your email
            </p>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading || verificationCode.length !== 6}
              className="btn-primary w-full"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <LoadingSpinner size="sm" className="mr-2" />
                  Verifying...
                </div>
              ) : (
                'Verify email'
              )}
            </button>
          </div>
        </form>

        <div className="bg-card rounded-lg border p-6 space-y-4">
          <div className="flex items-start space-x-3">
            <CheckCircleIcon className="h-5 w-5 text-accent mt-0.5 flex-shrink-0" />
            <div className="text-sm text-muted-foreground">
              Check your spam folder if you don't see the email
            </div>
          </div>
          
          <div className="flex items-start space-x-3">
            <CheckCircleIcon className="h-5 w-5 text-accent mt-0.5 flex-shrink-0" />
            <div className="text-sm text-muted-foreground">
              The verification code will expire in 24 hours
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="text-center">
            <p className="text-sm text-muted-foreground">
              Didn't receive the email?{' '}
              <button 
                className="font-medium text-primary hover:text-primary/80 disabled:opacity-50"
                onClick={handleResendCode}
                disabled={isResending}
              >
                {isResending ? 'Sending...' : 'Resend verification code'}
              </button>
            </p>
          </div>
          
          <div className="text-center">
            <Link 
              to="/auth/login" 
              className="text-sm font-medium text-primary hover:text-primary/80"
            >
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
